import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import ShipmentUpdateFlag from '../../../../General/ShipmentUpdateFlag';
import { fetchSkulabsData, configureStore, resetConfigureState } from './skulabsSlice';

const Item = styled(Paper)(({ theme }) => ({
    ...theme.typography.body2,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
    height: '100%'
}));

const SkuLabsIntegration = ({ storeName, platformName, storeParam, storeId }) => {
    const dispatch = useDispatch();

    const profile = useSelector(state => state.auth);
    const progress = useSelector(state => state.skulabs && state.skulabs.progress);
    const configure = useSelector(state => state.skulabs && state.skulabs.configure);
    const [alert, setAlert] = useState(false);
    const [snackOpen, setSnackOpen] = useState(false);
    const [values, setValues] = useState(progress.data);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (progress && progress.data && progress.data.access_token) {
            setValues({
                ...values,
                ...progress.data
            })
        }
    }, [progress && progress.data])

    useEffect(() => {
        if (configure && configure.message) {
            setAlert(true)
            setSnackOpen(true)
        } else if (configure && configure.hasErrors == true) {
            setAlert(true)
            setSnackOpen(true)
        }
    }, [dispatch, configure && configure.message])

    useEffect(() => {
        if (storeName) {
            let apikey = profile.profile.api_token
            const data = { apikey: apikey, store_name: storeName, store_id: storeId }
            dispatch(fetchSkulabsData(data));
        }
    }, [dispatch]);

    const validator = (fieldValues) => {
        let temp = { ...errors }
        if ('access_token' in fieldValues)
            temp.access_token = fieldValues.access_token ? "" : "This field is required."

        setErrors({
            ...temp
        })

        if (fieldValues === values)
            return Object.values(temp).every(x => x == "")
    }

    const onInputChange = e => {
        const { name, value } = e.target
        setValues({
            ...values,
            [name]: value
        })
        validator({ [name]: value })
    }

    const updateCallBackValue = (updateValue) => {
        setValues({
            ...values,
            ['is_update_shipment']: updateValue.is_update_shipment,
            ['is_update_shipment_on_create']: updateValue.is_update_shipment_on_create,
            ['is_update_shipment_on_close']: updateValue.is_update_shipment_on_close,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault()
        const isValid = validator(values)
        if (isValid) {
            dispatch(configureStore(values));
        }
    }

    const handleClose = () => {
        dispatch(resetConfigureState())
        setSnackOpen(false);
    };
    console.log(values);
    return (
        <>
            {alert &&
                <Snackbar
                    open={snackOpen}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right"
                    }}
                    autoHideDuration={6000}
                    onClose={handleClose}
                >
                    <Alert
                        variant="filled"
                        severity=
                        {configure && configure.hasErrors === true ? "error" : "success"}
                        sx={{ 
                            justifyContent: 'center', 
                            margin: "10px 0",
                            borderRadius: '12px',
                            fontWeight: 600,
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)'
                        }}
                    >
                        <span > {configure && configure.message}</span>
                    </Alert>
                </Snackbar>
            }
            {progress && progress.loading === false &&
                <Grid container spacing={0} justifyContent="center" sx={{ my: 4 }}>
                    <Grid item xs={12} sm={10} md={8} lg={7}>
                        <Card
                            sx={{
                                p: { xs: 3, md: 5 },
                                borderRadius: '16px',
                                border: '1px solid #E2E8F0',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01)',
                                backgroundColor: '#FFFFFF'
                            }}
                        >
                            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                                <Item>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 3.5 }}>
                                        
                                        {/* App Brand Identity Section Logo Graphic */}
                                        <Box sx={{ p: 2, backgroundColor: '#F8FAFC', borderRadius: '14px', border: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Box
                                                component="img"
                                                src="/assets/images/integration/SKULABS.png"
                                                alt="SKULABS"
                                                sx={{ width: 'auto', height: '44px', objectFit: 'contain' }}
                                            />
                                        </Box>

                                        {/* Module Context Headline Header Information */}
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '18px', mb: 0.5, letterSpacing: '-0.3px' }}>
                                                Connect SkuLabs Account
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#64748B', maxWidth: '380px', mx: 'auto', lineHeight: 1.5 }}>
                                                Provide your secure SkuLabs developer authorization token credentials below to map dynamic inventory and warehouse order items.
                                            </Typography>
                                        </Box>

                                        {/* Access Token Form Input Field Element Container */}
                                        <Box className="sc--form--center" sx={{ width: '100%', maxWidth: '440px', mt: 1 }}>
                                            <TextField
                                                label="Access Token"
                                                name="access_token"
                                                size="small"
                                                value={values.access_token}
                                                onChange={onInputChange}
                                                error={!!errors.access_token}
                                                helperText={errors.access_token}
                                                variant="outlined"
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '10px',
                                                        '& fieldset': { borderColor: '#E2E8F0' },
                                                        '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                        '&.Mui-focused fieldset': { borderColor: '#1976d2' }
                                                    },
                                                    '& .MuiInputLabel-root': { color: '#64748B', fontWeight: 500 },
                                                    '& .MuiFormHelperText-root': { mx: 0.5, mt: 0.75, fontWeight: 500 }
                                                }}
                                            />
                                        </Box>

                                        {/* Dynamic Synchronization Update Flag Section Area */}
                                        <Box sx={{ width: '100%', borderTop: '1px solid #F1F5F9', pt: 2, mt: 1 }}>
                                            <ShipmentUpdateFlag
                                                updateFlag={values.is_update_shipment}
                                                onCreateFlag={values.is_update_shipment_on_create}
                                                onCloseFlag={values.is_update_shipment_on_close}
                                                updateCallBack={updateCallBackValue}
                                                message={"Do you want to update shipment information back to skulabs account?"}
                                            />
                                        </Box>

                                        {/* Action Button Interaction Submit Panel Container */}
                                        <Box className="sc--form--center" sx={{ width: '100%', display: 'flex', justifyContent: 'center', borderTop: '1px solid #F1F5F9', pt: 3 }}>
                                            <LoadingButton
                                                variant="contained"
                                                onClick={e => handleSubmit(e)}
                                                loading={configure && configure.loading}
                                                endIcon={<SendOutlinedIcon sx={{ fontSize: '15px !important', ml: 0.5 }} />}
                                                sx={{
                                                    px: 5,
                                                    py: 1.25,
                                                    borderRadius: '8px',
                                                    fontWeight: 700,
                                                    textTransform: 'none',
                                                    fontSize: '13.5px',
                                                    boxShadow: 'none',
                                                    backgroundColor: '#1976d2',
                                                    '&:hover': { backgroundColor: '#115293', boxShadow: 'none' },
                                                    '& .MuiLoadingButton-loadingIndicator': { color: '#94A3B8' }
                                                }}
                                            >
                                                {(storeName) ? "Configure" : "Authorize"}
                                            </LoadingButton>
                                        </Box>

                                    </Box>
                                </Item>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            }
        </>
    )
}

export default SkuLabsIntegration;